// ────────────────────────────────────────────────────────────────────────────
// The editor's model ↔ the API.
//
// The screen keeps the prototype's names ('yesNo', 'arrival', 'planner'); the
// wire uses the backend's enum names ('YesNo', 'OnArrival', 'Planner'). Kept in
// one place so neither side has to know the other's spelling.
//
// Per-kind settings travel as a JSON object string. Only the settings the kind
// uses are written, so a stored row says what it means; reading falls back to
// the defaults for anything missing or unreadable rather than failing the page.
// ────────────────────────────────────────────────────────────────────────────
import type {
  CargoPropertyValue,
  TripCheckpoint,
  TripCheckpointDueValue,
  TripCheckpointInput,
  TripCheckpointKindValue,
  TripCheckpointMomentValue,
  TripCheckpointRoleValue,
  TripCheckpointSectionInput,
  TripCheckpointTemplate,
  TripCheckpointTemplateFields,
  TripCheckpointWriteBackValue,
} from '@my/tms-api';
import {
  DEFAULT_CONFIG,
  isNewId,
  type CargoProperty,
  type Checkpoint,
  type CheckpointConfig,
  type CheckpointKind,
  type CheckpointTemplate,
  type Due,
  type Moment,
  type Role,
  type Section,
  type SectionScope,
  type WriteBack,
} from './model';

const upper = <T extends string>(s: string) => `${s[0]!.toUpperCase()}${s.slice(1)}` as T;
const lower = <T extends string>(s: string) => `${s[0]!.toLowerCase()}${s.slice(1)}` as T;

const MOMENT_TO_WIRE: Record<Moment, TripCheckpointMomentValue> = {
  arrival: 'OnArrival',
  during: 'During',
  departure: 'BeforeDeparture',
};
const MOMENT_FROM_WIRE: Record<TripCheckpointMomentValue, Moment> = {
  OnArrival: 'arrival',
  During: 'during',
  BeforeDeparture: 'departure',
};

export const roleToWire = (r: Role) => upper<TripCheckpointRoleValue>(r);
export const roleFromWire = (r: TripCheckpointRoleValue) => lower<Role>(r);

// ── Per-kind settings ───────────────────────────────────────────────────────

function serialiseConfig(kind: CheckpointKind, c: CheckpointConfig): string | null {
  switch (kind) {
    case 'photo':
      return JSON.stringify({ minPhotos: Math.max(1, Math.floor(c.minPhotos) || 1) });
    case 'reading':
      return JSON.stringify({ unit: c.unit.trim() || DEFAULT_CONFIG.unit });
    case 'quantity':
      return JSON.stringify({ unit: c.unit.trim() || DEFAULT_CONFIG.unit, compareToPlanned: c.compareToPlanned });
    case 'temperature':
      return JSON.stringify(
        c.tempRange === 'cargo'
          ? { tempRange: 'cargo', toleranceC: c.toleranceC }
          : { tempRange: 'fixed', minC: c.minC, maxC: c.maxC },
      );
    case 'identifier':
      return JSON.stringify({ identifierOf: c.identifierOf, compareToPlanned: c.compareToPlanned });
    default:
      return null;
  }
}

function parseConfig(json: string | null): CheckpointConfig {
  const config = { ...DEFAULT_CONFIG };
  if (!json) return config;
  let raw: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return config;
    raw = parsed as Record<string, unknown>;
  } catch {
    return config;
  }
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  if (num(raw.minPhotos) !== null) config.minPhotos = Math.max(1, Math.floor(num(raw.minPhotos)!));
  if (typeof raw.unit === 'string' && raw.unit.trim()) config.unit = raw.unit;
  if (typeof raw.compareToPlanned === 'boolean') config.compareToPlanned = raw.compareToPlanned;
  if (raw.tempRange === 'cargo' || raw.tempRange === 'fixed') config.tempRange = raw.tempRange;
  if (num(raw.toleranceC) !== null) config.toleranceC = num(raw.toleranceC)!;
  if (num(raw.minC) !== null) config.minC = num(raw.minC)!;
  if (num(raw.maxC) !== null) config.maxC = num(raw.maxC)!;
  if (raw.identifierOf === 'bic' || raw.identifierOf === 'seal' || raw.identifierOf === 'other')
    config.identifierOf = raw.identifierOf;
  return config;
}

// ── From the API ────────────────────────────────────────────────────────────

/** A stored checkpoint (or an assembled one from the preview) in the editor's terms. */
export function checkpointFromWire(c: TripCheckpoint): Checkpoint {
  return {
    id: c.id,
    label: c.label,
    hint: c.hint ?? '',
    kind: lower<CheckpointKind>(c.kind),
    roles: c.roles.map(roleFromWire),
    required: c.required,
    // Both always set: the editor keeps the other one so a checkpoint can be
    // moved between scopes without losing it. Only the scope's one is sent.
    due: c.due ? lower<Due>(c.due) : 'beforeInProgress',
    moment: c.moment ? MOMENT_FROM_WIRE[c.moment] : 'during',
    writeBack: lower<WriteBack>(c.writeBack),
    waivable: c.waivable,
    config: parseConfig(c.configJson),
  };
}

export function templateFromWire(t: TripCheckpointTemplate): CheckpointTemplate {
  return {
    id: t.id,
    name: t.name,
    description: t.description ?? '',
    active: t.status === 'Active',
    appliesWhen: {
      cargoTypes: [...t.cargoTypes],
      cargoDefinitionIds: [...t.cargoDefinitionIds],
      serviceTagIds: [...t.serviceTagIds],
      properties: t.cargoProperties.map((p) => lower<CargoProperty>(p)),
    },
    sections: [...t.sections]
      .sort((a, b) => a.position - b.position)
      .map(
        (s): Section => ({
          id: s.id,
          scope: lower<SectionScope>(s.scope),
          name: s.name ?? '',
          taskTypes: [...s.taskTypes],
          checkpoints: [...s.checkpoints].sort((a, b) => a.position - b.position).map(checkpointFromWire),
        }),
      ),
  };
}

// ── To the API ──────────────────────────────────────────────────────────────

function checkpointToWire(c: Checkpoint, scope: SectionScope): TripCheckpointInput {
  return {
    id: isNewId(c.id) ? null : c.id,
    label: c.label.trim(),
    hint: c.hint.trim() || null,
    kind: upper<TripCheckpointKindValue>(c.kind),
    roles: c.roles.map(roleToWire),
    required: c.required,
    due: scope === 'order' ? upper<TripCheckpointDueValue>(c.due) : null,
    moment: scope === 'task' ? MOMENT_TO_WIRE[c.moment] : null,
    writeBack: upper<TripCheckpointWriteBackValue>(c.writeBack),
    waivable: c.waivable,
    configJson: serialiseConfig(c.kind, c.config),
  };
}

/** The body shared by create, update and the preview's draft. */
export function templateToFields(t: CheckpointTemplate): TripCheckpointTemplateFields {
  return {
    name: t.name.trim(),
    description: t.description.trim() || null,
    status: t.active ? 'Active' : 'Inactive',
    cargoTypes: [...t.appliesWhen.cargoTypes],
    cargoDefinitionIds: [...t.appliesWhen.cargoDefinitionIds],
    serviceTagIds: [...t.appliesWhen.serviceTagIds],
    cargoProperties: t.appliesWhen.properties.map((p) => upper<CargoPropertyValue>(p)),
    sections: t.sections.map(
      (s): TripCheckpointSectionInput => ({
        id: isNewId(s.id) ? null : s.id,
        scope: upper(s.scope),
        name: s.scope === 'task' ? s.name.trim() || null : null,
        taskTypes: s.scope === 'task' ? [...s.taskTypes] : [],
        checkpoints: s.checkpoints.map((c) => checkpointToWire(c, s.scope)),
      }),
    ),
  };
}
